import React from "react";
import {
    Button, CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Hidden,
    Modal,
    Typography
} from "@material-ui/core";
import DisposableImage from "./DisposableImage";
import {ImageButton, ImageUploadButton} from "./ImageButton";
import {makeStyles} from "@material-ui/styles";
import Rest from "../../../../helpers/Rest";
import FullScreenPopup from "../../components/FullScreenPopup";

const useStyle = makeStyles(() => {
    return {
        root: {
            "& .MuiGrid-item":{
                minWidth: 128
            }
        }
    }
})

const MAX_IMAGE_WIDTH = 1920
const MAX_IMAGE_HEIGHT = MAX_IMAGE_WIDTH * 2
const MAX_THUMBNAIL_DIMENSION = 216

function toDataUrl(base64){
    return "data:image/jpeg;base64," + base64
}

function dataUrlToBase64(url){
    return url.substring(url.indexOf(',') + 1)
}

function path(deviceId){
    return `device/${deviceId}/photo`
}

export function getDevicePhotos(deviceId){
    return Rest.get(`${path(deviceId)}/list`)
}

function blobKey(id){
    return 'blob' + id
}

function computeCrop(width, height, maxWidth, maxHeight){
    const wScale = maxWidth / width, hScale = maxHeight / height
    const scale = wScale > hScale ? wScale : hScale
    const wScaled = width * scale, hScaled = height * scale
    const offX = (wScaled - maxWidth) / scale, offY = (hScaled - maxHeight) / scale
    return {
        x: offX * 0.5,
        y: offY * 0.5,
        width: width - offX,
        height: height - offY
    }
}

class ImagesButtonImpl extends React.Component{
    constructor(props) {
        super(props);
        this.canvasRef = React.createRef()
        const image = new Image()
        let upload;
        image.addEventListener('load', () => {
            this.saveImage(image, upload)
        })
        this.state = {
            dialogOpen: false,
            viewingImage: false,
            images: []
        }
        this.toggle = () => this.setState({
            dialogOpen: !this.state.dialogOpen
        })
        this.add = added => {
            upload = added
            image.src = added.dataUrl
        }
        this.closeBlowUp = () => this.setState({
            viewingImage: false,
            blowUp: null
        })
    }

    fetchImage(id){
        return new Promise((ok) => {
            Rest.getBinary(`${this.path}/${id}`).then(blob => {
                const fileReader = new FileReader();
                fileReader.onload = e => {
                    ok(dataUrlToBase64(e.target.result))
                }
                fileReader.readAsDataURL(blob)
            })
        })
    }

    checkPhotos(photos, photoType){
        const filtered = photos.filter(image => image.photo_type === photoType)
        filtered.forEach(image => {
            if (!image.thumbnail) {
                this.fetchImage(image.id).then(data => image.thumbnail = data)
            }
        })
        this.setState({
            images: filtered
        })
    }

    componentDidMount() {
        this.checkPhotos(this.props.photos, this.props.photoType)
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const {photos, photoType} = this.props
        if (prevProps.photos !== photos || prevProps.photoType !== photoType){
            this.checkPhotos(photos, photoType)
        }
    }

    processImage(upload, image, type, maxWidth, maxHeight, cover){
        const {width, height} = image
        if (type === 'image/jpeg' && width <= maxWidth && height <= maxHeight){
            return upload.base64
        }
        const canvas = this.canvasRef.current
        const context = canvas.getContext("2d")
        if (cover) {
            canvas.width = maxWidth
            canvas.height = maxHeight
            const crop = computeCrop(width, height, maxWidth, maxHeight)
            context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, maxWidth, maxHeight)
        }
        else{
            let outWidth = width, outHeight = height
            if (width > maxWidth){
                outHeight *= maxWidth / width
                outWidth = maxWidth
            }
            if (outHeight > maxHeight){
                outWidth *= maxHeight / outHeight
                outHeight = maxHeight
            }
            canvas.width = outWidth
            canvas.height = outHeight
            context.drawImage(image, 0, 0, width, height, 0, 0, outWidth, outHeight)
        }
        return dataUrlToBase64(canvas.toDataURL("image/jpeg", 90))
    }

    saveImage(image, upload){
        const {fileName, type} = upload
        const send = {
            title: fileName,
            photo_type: this.props.photoType,
            photo: this.processImage(upload, image, type, MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT),
            thumbnail: this.processImage(upload, image, type, MAX_THUMBNAIL_DIMENSION, MAX_THUMBNAIL_DIMENSION)
        }
        this.setState({
            images: this.state.images.concat([send])
        })
        Rest.post(this.path, send).then(reply => send.id = reply.id)
    }

    get path(){
        return path(this.props.deviceId)
    }

    remove(image){
        Rest.del(`${this.path}/${image.id}`).then(() => console.log(`Deleted image ${image.id}`))
        const next = this.state.images.filter(item => item !== image)
        this.setState({
            images: next,
            dialogOpen: !!next.length
        })
    }

    doBlowUp(image){
        const {photo, id} = image
        this.setState({
            viewingImage: true,
            blowUp: photo && toDataUrl(photo)
        })
        if (!photo) {
            this.fetchImage(id).then(data => this.setState({
                blowUp: toDataUrl(image.photo = data)
            }))
        }
    }

    content(images){
        const {imageWidth, imageHeight} = this.props
        return <>
            {images.map((image, idx) => {
                const url = toDataUrl(image.thumbnail)
                return <Grid item xs={6} sm={3} key={image.id || `new-${idx}-${(new Date()).getTime()}`}>
                    <DisposableImage
                        src={url}
                        onClick={() => this.doBlowUp(image)} onDelete={() => this.remove(image)}
                        width={imageWidth}
                        height={imageHeight}
                        color="primary"
                    />
                </Grid>
            })}
            <Grid item xs={6} sm={3}>
                <ImageUploadButton text="Add photo" onSelected={this.add} />
            </Grid>
        </>
    }

    closeButton(){
        return <Button
            variant="contained"
            color="primary"
            onClick={this.toggle}
        >Close</Button>
    }

    dialog(images){
        const {text, className} = this.props
        return <Dialog open={this.state.dialogOpen} onClose={this.toggle} maxWidth="md">
            <DialogTitle>{text}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} className={className}>
                    {this.content(images)}
                </Grid>
            </DialogContent>
            <DialogActions>
                {this.closeButton()}
            </DialogActions>
        </Dialog>
    }

    modal(images){
        const {text, className} = this.props
        return <FullScreenPopup open={this.state.dialogOpen}>
            <Grid container spacing={2} className={className}>
                <Grid item xs={12}>
                    <Typography variant="h6">{text}</Typography>
                </Grid>
                {this.content(images)}
                <Grid item container xs={12} justify="flex-end">
                    {this.closeButton()}
                </Grid>
            </Grid>
        </FullScreenPopup>
    }

    blowUpImage(blowUp){
        if (!blowUp){
            return <CircularProgress/>
        }
        let maxWidth = window.innerWidth * 0.75
        if (maxWidth > MAX_IMAGE_WIDTH){
            maxWidth = MAX_IMAGE_WIDTH
        }
        return <img src={blowUp} alt="view" style={{
            maxWidth: maxWidth,
            display: "block",
            margin: "auto"
        }}/>
    }

    closeBlowUpButton(){
        return <Button
            variant="contained"
            color="primary"
            onClick={this.closeBlowUp}
        >Close</Button>
    }

    blowUpDesktop(blowUp){
        return <Dialog open onClose={this.closeBlowUp}>
            <DialogContent>
                {this.blowUpImage(blowUp)}
            </DialogContent>
            <DialogActions>
                {this.closeBlowUpButton()}
            </DialogActions>
        </Dialog>
    }

    blowUpMobile(blowUp){
        return <FullScreenPopup open>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    {this.blowUpImage(blowUp)}
                </Grid>
                <Grid item container xs={12} justify="flex-end">
                    {this.closeBlowUpButton()}
                </Grid>
            </Grid>
        </FullScreenPopup>
    }

    canvas(){
        return <canvas
            ref={this.canvasRef}
            width={MAX_IMAGE_WIDTH}
            height={MAX_IMAGE_HEIGHT}
            style={{
                position: "fixed",
                top: -MAX_IMAGE_HEIGHT,
                left: -MAX_IMAGE_WIDTH
            }}
        />
    }

    render() {
        const {text} = this.props
        const {images, blowUp} = this.state
        const filtered = images.filter(
            image => image.thumbnail || (
                image.thumbnail = (image.photo || (image.photo = this.state[blobKey(image.id)]))
            )
        )
        if (filtered.length){
            return <>
                <ImageButton text={text} onClick={this.toggle} imageUrl={toDataUrl(filtered[0].thumbnail)}/>
                <Hidden xsDown>
                    {this.dialog(filtered)}
                    {blowUp ? this.blowUpDesktop(blowUp) : null}
                </Hidden>
                <Hidden smUp>
                    {this.modal(filtered)}
                    {blowUp ? this.blowUpMobile(blowUp) : null}
                </Hidden>
                {this.canvas()}
            </>
        }
        else{
            return <>
                <ImageUploadButton text={text} onSelected={this.add}/>
                {this.canvas()}
            </>
        }
    }
}

export function ImagesButton({text, deviceId, photos, photoType, imageWidth, imageHeight}){
    const classes = useStyle()
    return <ImagesButtonImpl
        text={text}
        deviceId={deviceId}
        className={classes.root}
        photos={photos}
        photoType={photoType}
        imageWidth={imageWidth}
        imageHeight={imageHeight}
    />
}
